"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Info, X } from "lucide-react";
import { cn } from "@/lib/utils";

export type AppToastVariant = "success" | "error" | "info" | "warning";

export type AppToastItem = {
  id: string;
  message: string;
  title?: string;
  variant?: AppToastVariant;
};

type ShowToastInput = {
  message: string;
  title?: string;
  variant?: AppToastVariant;
  durationMs?: number;
};

type AppToastViewportProps = {
  toasts: AppToastItem[];
  onClose: (id: string) => void;
  className?: string;
};

const variantClasses: Record<AppToastVariant, string> = {
  success: "border-[#bbf7d0] bg-[#f0fdf4] text-[#16a34a]",
  error: "border-[#fecaca] bg-[#fef2f2] text-[#ef4444]",
  info: "border-[#bae6fd] bg-[#f0f9ff] text-[#0284c7]",
  warning: "border-[#fde68a] bg-[#fffbeb] text-[#d97706]",
};

const variantIcon: Record<AppToastVariant, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: AlertTriangle,
  info: Info,
  warning: AlertTriangle,
};

export function useAppToast() {
  const [toasts, setToasts] = useState<AppToastItem[]>([]);
  const timersRef = useRef<Map<string, number>>(new Map());

  const removeToast = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const showToast = useCallback(
    ({ message, title, variant = "info", durationMs = 4000 }: ShowToastInput) => {
      const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      setToasts((current) => [...current, { id, message, title, variant }]);
      const timer = window.setTimeout(() => {
        removeToast(id);
      }, durationMs);
      timersRef.current.set(id, timer);
      return id;
    },
    [removeToast],
  );

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
    };
  }, []);

  return useMemo(
    () => ({
      toasts,
      showToast,
      removeToast,
    }),
    [toasts, showToast, removeToast],
  );
}

export function AppToastViewport({ toasts, onClose, className }: AppToastViewportProps) {
  if (!toasts.length) {
    return null;
  }

  return (
    <div className={cn("fixed right-4 top-4 z-[70] flex w-[min(28rem,calc(100vw-2rem))] flex-col gap-2", className)}>
      {toasts.map((toast) => {
        const variant = toast.variant ?? "info";
        const Icon = variantIcon[variant];
        return (
          <div
            key={toast.id}
            role="status"
            aria-live="polite"
            className={cn(
              "rounded-[9px] border px-4 py-3",
              variantClasses[variant],
            )}
            style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.10)" }}
          >
            <div className="flex items-start gap-3">
              <Icon className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="min-w-0 flex-1 space-y-1">
                {toast.title ? <p className="text-sm font-semibold">{toast.title}</p> : null}
                <p className="text-sm">{toast.message}</p>
              </div>
              <button
                type="button"
                onClick={() => onClose(toast.id)}
                className="rounded-md p-1 text-slate-500 transition hover:bg-white/70 hover:text-slate-700"
                aria-label="Fechar aviso"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
